/**
 * imageUploadUtils.ts
 *
 * Utility functions for handling image uploads during form submission.
 * Detects image fields, validates configuration, and uploads images before form submission.
 */

import { Field } from '../../types/types';
import { uploadImages, ImageField } from '../../services/imageUploadService';
import { toast } from 'react-toastify';

/**
 * Get all camera/image upload fields from the form
 */
export function getImageUploadFields(fields: Field[]): Field[] {
  return fields.filter(
    f => f.type === 'camera_upload' || f.type === 'image_upload'
  );
}

/**
 * Validate that all image fields have customApi configured
 */
export function validateImageFieldsConfiguration(
  imageFields: Field[]
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  imageFields.forEach(field => {
    if (!field.customApi?.enabled || !field.customApi?.url) {
      errors.push(
        `Image upload endpoint not configured for field: "${field.label}"`
      );
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Collect image files from answers and prepare them for upload
 * Handles File objects, CapturedImage objects, and arrays of CapturedImages
 */
export function prepareImagesForUpload(
  imageFields: Field[],
  answers: Record<string, any>
): ImageField[] {
  const imagesToUpload: ImageField[] = [];

  imageFields.forEach(field => {
    const value = answers[field.id];

    if (!value) return; // Skip null/undefined values

    if (!field.customApi?.url) {
      throw new Error(
        `No upload endpoint configured for field: ${field.label}`
      );
    }

    // Handle single File object
    if (value instanceof File) {
      imagesToUpload.push({
        fieldId: field.id,
        file: value,
        endpoint: field.customApi.url,
        config: field.customApi,
      });
      return;
    }

    // Handle CapturedImage object (single image with prompt)
    if (
      typeof value === 'object' &&
      'file' in value &&
      'promptId' in value &&
      value.file instanceof File
    ) {
      imagesToUpload.push({
        fieldId: field.id,
        file: value.file,
        endpoint: field.customApi.url,
        config: field.customApi,
      });
      return;
    }

    // Handle array of CapturedImage objects (multi-image capture)
    if (Array.isArray(value)) {
      value.forEach((item: any, index: number) => {
        if (
          typeof item === 'object' &&
          'file' in item &&
          'promptId' in item &&
          item.file instanceof File &&
          field.customApi // Ensure customApi is defined
        ) {
          imagesToUpload.push({
            fieldId: `${field.id}[${index}]`, // Use array notation to distinguish multiple uploads
            file: item.file,
            endpoint: field.customApi.url,
            config: field.customApi,
          });
        }
      });
      return;
    }
  });

  return imagesToUpload;
}

/**
 * Upload images and update answers with returned URLs
 * Called during form submission before sending to backend
 */
export async function handleImageUploads(
  imageFields: Field[],
  answers: Record<string, any>,
  getAuthHeaders: () => Promise<Record<string, string>>
): Promise<Record<string, any>> {
  // Check if there are any image fields at all
  if (imageFields.length === 0) {
    return answers; // No images to upload
  }

  // Validate configuration
  const validation = validateImageFieldsConfiguration(imageFields);
  if (!validation.valid) {
    const message = validation.errors.join('; ');
    toast.error(message);
    throw new Error(`Image upload configuration error: ${message}`);
  }

  // Prepare images for upload
  const imagesToUpload = prepareImagesForUpload(imageFields, answers);

  // If no files are selected, no need to upload
  if (imagesToUpload.length === 0) {
    return answers;
  }

  // Upload images
  console.log(
    `📸 Uploading ${imagesToUpload.length} image(s) to ${new Set(imagesToUpload.map(i => i.endpoint)).size} endpoint(s)...`
  );

  try {
    const uploadedUrls = await uploadImages(imagesToUpload, getAuthHeaders);

    // Update answers with returned URLs
    const updatedAnswers = { ...answers };

    // Group uploaded URLs by field ID (handle array notation for multi-image)
    const groupedUrls: Record<string, string[]> = {};
    Object.entries(uploadedUrls).forEach(([key, url]) => {
      // Check if this is an array item (e.g., "fieldId[0]")
      const arrayMatch = key.match(/^(.+)\[\d+\]$/);
      if (arrayMatch) {
        const fieldId = arrayMatch[1];
        if (!groupedUrls[fieldId]) {
          groupedUrls[fieldId] = [];
        }
        groupedUrls[fieldId].push(url);
      } else {
        // Single image - store as string directly
        updatedAnswers[key] = url;
        console.log(`✅ Image for field ${key} uploaded to: ${url}`);
      }
    });

    // For multi-image uploads, reconstruct arrays and store them
    Object.entries(groupedUrls).forEach(([fieldId, urls]) => {
      updatedAnswers[fieldId] = urls;
      console.log(`✅ Images for field ${fieldId} uploaded: ${urls.join(', ')}`);
    });

    toast.success(
      `✅ ${imagesToUpload.length} image(s) uploaded successfully`
    );

    return updatedAnswers;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error('❌ Image upload failed:', message);
    toast.error("Image upload failed");
    throw error;
  }
}
